/**
 * MultiDayVerticalView Component
 * Universal vertical calendar view supporting 1-7 days with configurable offset
 * Replaces DailyView and WeeklyView with a single flexible component
 */

import React, { useRef, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { Spin, Tooltip, Button, Avatar } from 'antd';
import { LeftOutlined, RightOutlined, UserOutlined } from '@ant-design/icons';
import { useUserTimezone } from '@/hooks/useUserTimezone';
import type { CalendarEvent } from '@/api/types/calendars.types';
import { useSunTimes, useWeatherForecast } from '@/hooks/useCalendars';
import { SunTimesOverlay } from './SunTimesOverlay';
import {
  getVisibleDays,
  isToday,
  getEventsForDay,
  getTodayIndex,
} from './utils/multiDayHelpers';
import './MultiDayVerticalView.css';

/** Selected airport with timezone info */
interface SelectedAirport {
  id: string;
  name: string;
  timezone?: string;
}

interface MultiDayVerticalViewProps {
  /** Base date (usually today) */
  currentDate: Date;
  /** Day offset from currentDate (negative = past, 0 = today, positive = future) */
  dayOffset: number;
  /** Number of consecutive days to display */
  dayCount: number;
  /** Events to display */
  events: CalendarEvent[];
  /** Loading state */
  loading?: boolean;
  /** Callback when event is clicked */
  onEventClick?: (event: CalendarEvent) => void;
  /** Callback when time slot is clicked */
  onSlotClick?: (date: Date, hour: number) => void;
  /** Callback when event is updated via drag & drop */
  onEventUpdate?: (
    eventId: string,
    newStartTime: string,
    newEndTime: string
  ) => Promise<void>;
  /** Callback when date changes via navigation */
  onDateChange?: (date: Date) => void;
  /** Airport ID for sun times (optional) */
  airportId?: string;
  /** Whether to show sunrise/sunset overlay */
  showDayNight?: boolean;
  /** Whether to show weather forecast */
  showWeather?: boolean;
  /** Weather coordinates */
  weatherCoordinates?: {
    latitude: number | undefined;
    longitude: number | undefined;
    airportName: string;
  } | null;
  /** Selected airports with timezone info for multi-timezone display */
  selectedAirports?: SelectedAirport[];
}

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const HOUR_HEIGHT = 60; // pixels per hour

/**
 * PersonAvatar Component
 * Displays person avatar with fallback to initials
 */
const PersonAvatar: React.FC<{
  personId: string;
  personName: string;
  size?: number;
  backgroundColor?: string;
  style?: React.CSSProperties;
}> = ({
  personId,
  personName,
  size = 24,
  backgroundColor = '#667eea',
  style,
}) => {
  const [avatarError, setAvatarError] = React.useState(false);

  const getInitials = () => {
    const nameParts = personName.split(' ');
    if (nameParts.length >= 2) {
      return `${nameParts[0][0]}${nameParts[1][0]}`.toUpperCase();
    }
    return personName.substring(0, 2).toUpperCase();
  };

  return (
    <Avatar
      size={size}
      src={
        !avatarError
          ? `/v2/api/tenants/users/id/${personId}/avatar.jpg?resize_to=64x64`
          : undefined
      }
      icon={avatarError && <UserOutlined />}
      onError={() => {
        setAvatarError(true);
        return false;
      }}
      style={{
        backgroundColor,
        fontSize: size > 24 ? '14px' : '10px',
        fontWeight: 600,
        ...style,
      }}
    >
      {avatarError && getInitials()}
    </Avatar>
  );
};

// Legacy alias for backwards compatibility
const InstructorAvatar: React.FC<{
  instructorId: string;
  instructorName: string;
}> = ({ instructorId, instructorName }) => (
  <PersonAvatar
    personId={instructorId}
    personName={instructorName}
    size={24}
    backgroundColor="#667eea"
    style={{ marginRight: '8px' }}
  />
);

/**
 * Generate tooltip content for event
 * Format: Vreme, Student (with avatar), Instruktor (with avatar), Aerodrom
 */
const generateEventTooltip = (
  event: CalendarEvent,
  formatTime: (date: string, format: string) => string,
  formatDate: (date: string, format: string) => string,
  userTimezone: string
): React.ReactNode => {
  const airportResource = event.resources?.find(
    (r) => r.resource_type === 'AIRPORT'
  );
  const airportTimezone = airportResource?.resource_timezone;

  // Format: DD.MM.YY HH:mm - HH:mm
  const dateStr = formatDate(event.start_time, 'DD.MM.YY');
  const startTime = formatTime(event.start_time, 'HH:mm');
  const endTime = formatTime(event.end_time, 'HH:mm');

  // Get student and instructor resources
  const studentResource = event.resources?.find((r) =>
    ['PILOT', 'PASSENGER', 'PERSON', 'STUDENT'].includes(r.resource_type)
  );
  const instructorResource = event.resources?.find(
    (r) => r.resource_type === 'INSTRUCTOR'
  );

  // Airport info with timezone
  let airportDisplay = null;
  if (airportResource) {
    const airportName = airportResource.resource_name;
    const shortCode = airportResource.metadata?.icao_code || '';
    let tzInfo = '';
    if (airportTimezone) {
      const tzAbbr = dayjs().tz(airportTimezone).format('z');
      tzInfo = ` (${tzAbbr})`;
    }
    airportDisplay = shortCode
      ? `${airportName} (${shortCode})${tzInfo}`
      : `${airportName}${tzInfo}`;
  }

  return (
    <div style={{ maxWidth: '320px' }}>
      {/* Time row */}
      <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center' }}>
        <strong style={{ marginRight: 8 }}>Vreme:</strong>
        <span>
          {dateStr} {startTime} - {endTime}
        </span>
      </div>

      {/* Student row with avatar */}
      {studentResource && (
        <div
          style={{
            marginBottom: 8,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <strong style={{ marginRight: 8 }}>Student:</strong>
          <PersonAvatar
            personId={studentResource.resource_id}
            personName={studentResource.resource_name}
            size={20}
            backgroundColor="#52c41a"
            style={{ marginRight: 6 }}
          />
          <span>{studentResource.resource_name}</span>
        </div>
      )}

      {/* Instructor row with avatar */}
      {instructorResource && (
        <div
          style={{
            marginBottom: 8,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <strong style={{ marginRight: 8 }}>Instruktor:</strong>
          <PersonAvatar
            personId={instructorResource.resource_id}
            personName={instructorResource.resource_name}
            size={20}
            backgroundColor="#667eea"
            style={{ marginRight: 6 }}
          />
          <span>{instructorResource.resource_name}</span>
        </div>
      )}

      {/* Airport row */}
      {airportDisplay && (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <strong style={{ marginRight: 8 }}>Aerodrom:</strong>
          <span>{airportDisplay}</span>
        </div>
      )}
    </div>
  );
};

/**
 * Get event position and height in user's timezone
 */
const getEventPosition = (
  event: CalendarEvent,
  dayStart: dayjs.Dayjs,
  parse: (date: string) => dayjs.Dayjs
) => {
  const start = parse(event.start_time);
  const end = parse(event.end_time);

  const minutesFromDayStart = start.diff(dayStart, 'minute');
  const durationMinutes = end.diff(start, 'minute');

  return {
    top: (minutesFromDayStart / 60) * HOUR_HEIGHT,
    height: Math.max((durationMinutes / 60) * HOUR_HEIGHT, 20),
  };
};

/**
 * Get resource details from event
 */
const getEventDetails = (event: CalendarEvent) => {
  const resources = event.resources || [];
  const airplane = resources.find((r) => r.resource_type === 'AIRPLANE');
  const instructor = resources.find((r) => r.resource_type === 'INSTRUCTOR');
  const student = resources.find((r) =>
    ['PILOT', 'PASSENGER', 'PERSON'].includes(r.resource_type)
  );

  return { airplane, instructor, student };
};

/**
 * Get event color from airplane resource metadata
 */
const getEventColor = (event: CalendarEvent): string => {
  const airplane = event.resources?.find((r) => r.resource_type === 'AIRPLANE');
  const color = airplane?.metadata?.calendar_color;
  return color || '#1890ff';
};

/**
 * Calculate event positioning for overlapping events
 * Smart layout: only creates columns for events that actually overlap
 */
const calculateOverlapLayout = (
  events: CalendarEvent[],
  parse: (date: string) => dayjs.Dayjs
): Array<{ event: CalendarEvent; column: number; totalColumns: number }> => {
  if (events.length === 0) return [];

  const sortedEvents = [...events].sort((a, b) => {
    const aStart = parse(a.start_time);
    const bStart = parse(b.start_time);
    if (!aStart.isSame(bStart)) {
      return aStart.isBefore(bStart) ? -1 : 1;
    }
    const aDuration = parse(a.end_time).diff(aStart, 'minute');
    const bDuration = parse(b.end_time).diff(bStart, 'minute');
    return bDuration - aDuration;
  });

  const overlapGroups: Array<CalendarEvent[]> = [];

  sortedEvents.forEach((event) => {
    const eventStart = parse(event.start_time);
    const eventEnd = parse(event.end_time);

    let addedToGroup = false;
    for (const group of overlapGroups) {
      const overlapsWithGroup = group.some((groupEvent) => {
        const groupStart = parse(groupEvent.start_time);
        const groupEnd = parse(groupEvent.end_time);
        return eventStart.isBefore(groupEnd) && eventEnd.isAfter(groupStart);
      });

      if (overlapsWithGroup) {
        group.push(event);
        addedToGroup = true;
        break;
      }
    }

    if (!addedToGroup) {
      overlapGroups.push([event]);
    }
  });

  const result: Array<{
    event: CalendarEvent;
    column: number;
    totalColumns: number;
  }> = [];

  overlapGroups.forEach((group) => {
    if (group.length === 1) {
      result.push({
        event: group[0],
        column: 0,
        totalColumns: 1,
      });
    } else {
      const groupColumns: Array<{ event: CalendarEvent; end: dayjs.Dayjs }> =
        [];

      group.forEach((event) => {
        const start = parse(event.start_time);
        const end = parse(event.end_time);

        let column = 0;
        while (
          column < groupColumns.length &&
          groupColumns[column] &&
          start.isBefore(groupColumns[column].end)
        ) {
          column++;
        }

        groupColumns[column] = { event, end };

        result.push({
          event,
          column,
          totalColumns: 0,
        });
      });

      const maxColumns = groupColumns.filter((c) => c !== undefined).length;
      const groupStartIndex = result.length - group.length;
      for (let i = groupStartIndex; i < result.length; i++) {
        result[i].totalColumns = maxColumns;
      }
    }
  });

  return result;
};

export const MultiDayVerticalView: React.FC<MultiDayVerticalViewProps> = ({
  currentDate,
  dayOffset,
  dayCount,
  events,
  loading = false,
  onEventClick,
  onSlotClick,
  onEventUpdate,
  onDateChange,
  airportId,
  showDayNight = true,
  showWeather = false,
  weatherCoordinates = null,
  selectedAirports = [],
}) => {
  const { formatTime, formatDate, parse, timezone, toISOWithTimezone } =
    useUserTimezone();
  const currentTime = useMemo(() => dayjs().tz(timezone), [timezone]);
  const gridRef = useRef<HTMLDivElement>(null);

  // Calculate additional timezones to display (airports with different timezone than user)
  const additionalTimezones = useMemo(() => {
    console.log('[MultiDayVerticalView] selectedAirports:', selectedAirports);
    console.log('[MultiDayVerticalView] user timezone:', timezone);
    const result = selectedAirports
      .filter((airport) => airport.timezone && airport.timezone !== timezone)
      .map((airport) => ({
        timezone: airport.timezone!,
        name: airport.name,
        abbr: dayjs().tz(airport.timezone!).format('z'),
      }));
    console.log('[MultiDayVerticalView] additionalTimezones:', result);
    return result;
  }, [selectedAirports, timezone]);

  // State for current time line position
  const [currentTimePosition, setCurrentTimePosition] = useState<number | null>(
    null
  );

  // Drag & drop state
  const [draggedEvent, setDraggedEvent] = useState<CalendarEvent | null>(null);
  const draggedEventRef = useRef<CalendarEvent | null>(null); // Ref for sync access
  const [dragOffsetY, setDragOffsetY] = useState<number>(0);
  const dragOffsetYRef = useRef<number>(0); // Ref for sync access
  const [dropPreview, setDropPreview] = useState<{
    top: number;
    height: number;
    dayIndex: number;
    hasConflict: boolean;
  } | null>(null);

  // Hover highlight state - shows 1-hour slot on hover (snaps to 15-min intervals)
  const [hoverSlot, setHoverSlot] = useState<{
    dayIndex: number;
    minutes: number; // Minutes from midnight (0-1440), snapped to 15-min intervals
  } | null>(null);

  // Calculate visible days
  const visibleDays = useMemo(
    () => getVisibleDays(currentDate, dayOffset, dayCount, timezone),
    [currentDate, dayOffset, dayCount, timezone]
  );

  // Get today index
  const todayIndex = useMemo(
    () => getTodayIndex(visibleDays, timezone),
    [visibleDays, timezone]
  );

  // Fetch sun times for all visible days with a single range query
  // This is much more efficient than making separate API calls
  const { data: sunTimesData } = useSunTimes(
    airportId || '',
    {
      date_from: visibleDays[0]?.format('YYYY-MM-DD') || '',
      date_to: visibleDays[visibleDays.length - 1]?.format('YYYY-MM-DD') || '',
    },
    showDayNight && !!airportId && visibleDays.length > 0
  );

  // Create a map for quick lookup by date
  const sunTimesMap = useMemo(() => {
    if (!sunTimesData) return {};

    // Handle range response
    if ('items' in sunTimesData) {
      return Object.fromEntries(
        sunTimesData.items.map((item: any) => [item.date, item])
      );
    }

    // Handle single day response (backwards compatibility)
    if (visibleDays.length === 1 && sunTimesData) {
      return { [visibleDays[0].format('YYYY-MM-DD')]: sunTimesData };
    }

    return {};
  }, [sunTimesData, visibleDays]);

  // Fetch weather forecast
  const { data: weatherData } = useWeatherForecast(
    weatherCoordinates?.latitude,
    weatherCoordinates?.longitude,
    showWeather && !!weatherCoordinates
  );

  // Auto-scroll to 8 AM on mount
  useEffect(() => {
    if (gridRef.current && !loading) {
      gridRef.current.scrollTop = 8 * HOUR_HEIGHT;
    }
  }, [loading, currentDate]);

  // Update current time line position
  useEffect(() => {
    const updateCurrentTimePosition = () => {
      if (todayIndex >= 0) {
        const now = dayjs().tz(timezone);
        const hours = now.hour();
        const minutes = now.minute();
        const position = (hours + minutes / 60) * HOUR_HEIGHT;
        setCurrentTimePosition(position);
      } else {
        setCurrentTimePosition(null);
      }
    };

    updateCurrentTimePosition();
    const interval = setInterval(updateCurrentTimePosition, 60000);

    return () => clearInterval(interval);
  }, [todayIndex, timezone]);

  // Navigation handlers
  const handlePrevious = () => {
    if (onDateChange) {
      const newDate = dayjs(currentDate).subtract(dayCount, 'day').toDate();
      onDateChange(newDate);
    }
  };

  const handleNext = () => {
    if (onDateChange) {
      const newDate = dayjs(currentDate).add(dayCount, 'day').toDate();
      onDateChange(newDate);
    }
  };

  const handleToday = () => {
    if (onDateChange) {
      onDateChange(new Date());
    }
  };

  const handleSlotClick = (day: dayjs.Dayjs, minutes: number) => {
    if (onSlotClick) {
      const hour = Math.floor(minutes / 60);
      const mins = minutes % 60;
      const date = day.hour(hour).minute(mins).second(0).toDate();
      onSlotClick(date, hour);
    }
  };

  // Helper: Check if dropping at this time would create a resource conflict
  const checkResourceConflict = (
    newStartTime: dayjs.Dayjs,
    newEndTime: dayjs.Dayjs
  ): boolean => {
    if (!draggedEvent) return false;

    const draggedResourceIds = draggedEvent.resources
      .filter((r) => r.resource_type !== 'AIRPORT')
      .map((r) => r.resource_id);

    if (draggedResourceIds.length === 0) return false;

    return events.some((event) => {
      if (event.id === draggedEvent.id) return false;

      const hasSharedResource = event.resources.some(
        (r) =>
          r.resource_type !== 'AIRPORT' &&
          draggedResourceIds.includes(r.resource_id)
      );

      if (!hasSharedResource) return false;

      const eventStart = dayjs(event.start_time);
      const eventEnd = dayjs(event.end_time);

      return newStartTime.isBefore(eventEnd) && newEndTime.isAfter(eventStart);
    });
  };

  // Drag & drop handlers
  const handleEventDragStart = (
    event: CalendarEvent,
    dayIndex: number,
    e: React.DragEvent
  ) => {
    if (!onEventUpdate) return;

    const eventElement = e.currentTarget as HTMLElement;
    const rect = eventElement.getBoundingClientRect();
    const offsetY = e.clientY - rect.top;

    // Set both state and ref for sync access in dragOver
    setDraggedEvent(event);
    draggedEventRef.current = event;
    setDragOffsetY(offsetY);
    dragOffsetYRef.current = offsetY;
  };

  const handleDragOver = (dayIndex: number, e: React.DragEvent) => {
    e.preventDefault();

    // Use ref for sync access (state may not be updated yet)
    const currentDraggedEvent = draggedEventRef.current;
    if (!currentDraggedEvent) return;

    const dayColumn = e.currentTarget as HTMLElement;
    const rect = dayColumn.getBoundingClientRect();
    const relativeY = e.clientY - rect.top - dragOffsetYRef.current;

    // Snap to 15-minute intervals
    const snapInterval = 15;
    const minutesFromTop = (relativeY / HOUR_HEIGHT) * 60;
    const snappedMinutes =
      Math.round(minutesFromTop / snapInterval) * snapInterval;
    const snappedTop = (snappedMinutes / 60) * HOUR_HEIGHT;

    // Get event duration
    const originalStart = parse(currentDraggedEvent.start_time);
    const originalEnd = parse(currentDraggedEvent.end_time);
    const durationMinutes = originalEnd.diff(originalStart, 'minute');
    const eventHeight = (durationMinutes / 60) * HOUR_HEIGHT;

    // Calculate new start/end times for target day
    const targetDay = visibleDays[dayIndex].startOf('day');
    const hours = Math.floor(snappedMinutes / 60);
    const minutes = snappedMinutes % 60;
    const newStart = targetDay
      .hour(hours)
      .minute(minutes)
      .second(0)
      .millisecond(0);
    const newEnd = newStart.add(durationMinutes, 'minute');

    // Check for conflicts
    const hasConflict = checkResourceConflict(newStart, newEnd);

    setDropPreview({
      top: snappedTop,
      height: eventHeight,
      dayIndex,
      hasConflict,
    });
  };

  const handleDragLeave = () => {
    // Don't clear immediately - let dragOver update it
  };

  const handleEventDrop = async (dayIndex: number, e: React.DragEvent) => {
    if (!draggedEvent || !onEventUpdate) return;

    setDropPreview(null);

    try {
      const dayColumn = e.currentTarget as HTMLElement;
      const rect = dayColumn.getBoundingClientRect();
      const relativeY = e.clientY - rect.top - dragOffsetY;

      const snapInterval = 15;
      const minutesFromTop = (relativeY / HOUR_HEIGHT) * 60;
      const snappedMinutes =
        Math.round(minutesFromTop / snapInterval) * snapInterval;

      const targetDay = visibleDays[dayIndex].startOf('day');
      const hours = Math.floor(snappedMinutes / 60);
      const minutes = snappedMinutes % 60;
      const newStart = targetDay
        .hour(hours)
        .minute(minutes)
        .second(0)
        .millisecond(0);

      const originalStart = parse(draggedEvent.start_time);
      const originalEnd = parse(draggedEvent.end_time);
      const durationMinutes = originalEnd.diff(originalStart, 'minute');
      const newEnd = newStart.add(durationMinutes, 'minute');

      const hasConflict = checkResourceConflict(newStart, newEnd);
      if (hasConflict) {
        console.warn('Cannot drop: Resource conflict detected');
        return;
      }

      await onEventUpdate(
        draggedEvent.id,
        toISOWithTimezone(newStart),
        toISOWithTimezone(newEnd)
      );
    } catch (error) {
      console.error('Failed to update event:', error);
    } finally {
      setDraggedEvent(null);
    }
  };

  if (loading) {
    return (
      <div className="multi-day-vertical-view-loading">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div
      className="multi-day-vertical-view"
      style={{ ['--day-count' as string]: dayCount }}
    >
      {/* Header with day columns */}
      <div className="multi-day-header">
        {/* Corner with navigation buttons */}
        <div className="multi-day-header-corner">
          {onDateChange && (
            <>
              <Button
                type="text"
                size="small"
                icon={<LeftOutlined />}
                onClick={handlePrevious}
                className="multi-day-nav-button"
              />
              <Button
                type="text"
                size="small"
                onClick={handleToday}
                style={{ fontSize: '11px', padding: '4px 8px' }}
              >
                Today
              </Button>
              <Button
                type="text"
                size="small"
                icon={<RightOutlined />}
                onClick={handleNext}
                className="multi-day-nav-button"
              />
            </>
          )}
        </div>

        {/* Day headers */}
        {visibleDays.map((day, index) => {
          const isTodayDay = isToday(day, timezone);
          const dayForecast = weatherData?.forecasts.find(
            (f) => f.date === day.format('YYYY-MM-DD')
          );

          return (
            <div
              key={day.format('YYYY-MM-DD')}
              className={`day-header ${isTodayDay ? 'is-today' : ''}`}
            >
              <div className="day-name">{day.format('ddd')}</div>
              <div className="day-date">{day.format('D. MMM')}</div>

              {/* Weather info in header (only for multi-day views) */}
              {showWeather && dayCount > 1 && dayForecast && (
                <Tooltip
                  title={
                    <div>
                      <strong>{dayForecast.weather.description}</strong>
                      <div style={{ marginTop: 8 }}>
                        <div>
                          Max: {Math.round(dayForecast.temperature.max)}°C
                        </div>
                        <div>
                          Min: {Math.round(dayForecast.temperature.min)}°C
                        </div>
                        <div>Vlažnost: {dayForecast.humidity}%</div>
                        <div>
                          Vetar: {dayForecast.wind_speed.toFixed(1)} m/s
                        </div>
                      </div>
                    </div>
                  }
                  placement="bottom"
                  color="rgba(0, 0, 0, 0.85)"
                >
                  <div className="day-weather-info">
                    <img
                      src={`https://openweathermap.org/img/wn/${dayForecast.weather.icon}.png`}
                      alt={dayForecast.weather.description}
                      style={{ width: '24px', height: '24px' }}
                    />
                    <span className="day-weather-temp">
                      {Math.round(dayForecast.temperature.day)}°
                    </span>
                  </div>
                </Tooltip>
              )}
            </div>
          );
        })}
      </div>

      {/* Grid with time column and day columns */}
      <div className="multi-day-grid" ref={gridRef}>
        {/* Time column */}
        <div
          className={`time-column ${additionalTimezones.length > 0 ? 'has-additional-tz' : ''}`}
        >
          {HOURS.map((hour) => (
            <div key={hour} className="time-slot">
              {/* User's timezone (primary) */}
              <span className="time-label time-label-primary">
                {hour.toString().padStart(2, '0')}:00
              </span>
              {/* Additional airport timezones */}
              {additionalTimezones.map((tz) => {
                // Convert hour from user timezone to airport timezone
                const userTime = dayjs()
                  .tz(timezone)
                  .hour(hour)
                  .minute(0)
                  .second(0);
                const airportTime = userTime.tz(tz.timezone);
                const airportHour = airportTime.format('HH:mm');

                return (
                  <Tooltip
                    key={tz.timezone}
                    title={`${tz.name} (${tz.timezone})`}
                    placement="right"
                  >
                    <span className="time-label time-label-secondary">
                      {airportHour} ({tz.abbr})
                    </span>
                  </Tooltip>
                );
              })}
            </div>
          ))}
        </div>

        {/* Day columns */}
        {visibleDays.map((day, dayIndex) => {
          const isTodayDay = isToday(day, timezone);
          const dayEvents = getEventsForDay(events, day, timezone);
          // Get sun times for this specific day from the map
          const sunTimes = sunTimesMap[day.format('YYYY-MM-DD')];
          const todayForecast = weatherData?.forecasts.find(
            (f) => f.date === day.format('YYYY-MM-DD')
          );

          return (
            <div
              key={day.format('YYYY-MM-DD')}
              className={`day-column ${isTodayDay ? 'is-today' : ''}`}
            >
              {/* Hour slots (clickable) - fallback, main click handled by day-events-container */}
              {HOURS.map((hour) => (
                <div
                  key={hour}
                  className="hour-slot"
                  onClick={() => handleSlotClick(day, hour * 60)}
                />
              ))}

              {/* Events container - drag handlers here because it covers the column */}
              <div
                className="day-events-container"
                onDragOver={(e) => handleDragOver(dayIndex, e)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleEventDrop(dayIndex, e)}
                onMouseMove={(e) => {
                  // Track hover position for 1-hour highlight (snaps to 15-min intervals)
                  const target = e.target as HTMLElement;
                  // Don't show hover highlight over events
                  if (target.closest('.calendar-event')) {
                    setHoverSlot(null);
                    return;
                  }
                  const rect = e.currentTarget.getBoundingClientRect();
                  const y = e.clientY - rect.top;
                  // Convert Y to minutes and snap to 15-minute intervals
                  const totalMinutes = (y / HOUR_HEIGHT) * 60;
                  const snappedMinutes = Math.floor(totalMinutes / 15) * 15;
                  // Clamp to valid range (0 to 23:45 = 1425 minutes)
                  if (snappedMinutes >= 0 && snappedMinutes < 24 * 60) {
                    setHoverSlot({ dayIndex, minutes: snappedMinutes });
                  }
                }}
                onMouseLeave={() => setHoverSlot(null)}
                onClick={(e) => {
                  // Handle click on empty area - snap to 15-min intervals
                  const target = e.target as HTMLElement;
                  // Only handle clicks on the container itself or overlays, not on events
                  if (target.closest('.calendar-event')) return;

                  const rect = e.currentTarget.getBoundingClientRect();
                  const y = e.clientY - rect.top;
                  // Convert Y to minutes and snap to 15-minute intervals
                  const totalMinutes = (y / HOUR_HEIGHT) * 60;
                  const snappedMinutes = Math.floor(totalMinutes / 15) * 15;
                  if (snappedMinutes >= 0 && snappedMinutes < 24 * 60) {
                    handleSlotClick(day, snappedMinutes);
                  }
                }}
              >
                {/* Sun times overlay */}
                {showDayNight && sunTimes && (
                  <SunTimesOverlay sunTimes={sunTimes} />
                )}

                {/* Weather indicators - only for single day view (dayCount === 1) */}
                {showWeather && dayCount === 1 && todayForecast && (
                  <>
                    {[
                      {
                        hour: 0,
                        temp: todayForecast.temperature.night,
                        label: 'Ponoć (00h)',
                      },
                      {
                        hour: 6,
                        temp: todayForecast.temperature.morning,
                        label: 'Jutro (06h)',
                      },
                      {
                        hour: 12,
                        temp: todayForecast.temperature.day,
                        label: 'Podne (12h)',
                      },
                      {
                        hour: 18,
                        temp: todayForecast.temperature.evening,
                        label: 'Veče (18h)',
                      },
                    ].map(({ hour, temp, label }) => (
                      <Tooltip
                        key={hour}
                        title={
                          <div>
                            <strong>{label}</strong>
                            <div>{todayForecast.weather.description}</div>
                            <div>Temperatura: {Math.round(temp)}°C</div>
                            <div>Vlažnost: {todayForecast.humidity}%</div>
                            <div>
                              Vetar: {todayForecast.wind_speed.toFixed(1)} m/s
                            </div>
                          </div>
                        }
                        placement="right"
                        color="rgba(0, 0, 0, 0.85)"
                      >
                        <div
                          className="weather-time-marker"
                          style={{ top: `${hour * 60}px` }}
                        >
                          <img
                            src={`https://openweathermap.org/img/wn/${todayForecast.weather.icon}.png`}
                            alt="weather"
                            className="weather-time-icon"
                          />
                          <span className="weather-time-temp">
                            {Math.round(temp)}°
                          </span>
                        </div>
                      </Tooltip>
                    ))}
                  </>
                )}

                {/* Hover highlight - 1-hour slot indicator (snaps to 15-min) */}
                {hoverSlot &&
                  hoverSlot.dayIndex === dayIndex &&
                  !draggedEvent && (
                    <div
                      className="hover-slot-highlight"
                      style={{
                        position: 'absolute',
                        top: `${(hoverSlot.minutes / 60) * HOUR_HEIGHT}px`,
                        left: '4px',
                        right: '4px',
                        height: `${HOUR_HEIGHT}px`,
                        background: 'rgba(24, 144, 255, 0.15)',
                        border: '2px dashed rgba(24, 144, 255, 0.5)',
                        borderRadius: '6px',
                        pointerEvents: 'none',
                        zIndex: 5,
                        transition: 'top 0.1s ease-out',
                      }}
                    />
                  )}

                {/* Drop preview - dashed outline (red if conflict) - BEFORE events for z-index */}
                {dropPreview && dropPreview.dayIndex === dayIndex && (
                  <div
                    style={{
                      position: 'absolute',
                      top: `${dropPreview.top}px`,
                      left: '8px',
                      right: '8px',
                      height: `${dropPreview.height}px`,
                      border: dropPreview.hasConflict
                        ? '3px dashed #ff4d4f'
                        : '3px dashed #1890ff',
                      borderRadius: '6px',
                      backgroundColor: dropPreview.hasConflict
                        ? 'rgba(255, 77, 79, 0.15)'
                        : 'rgba(24, 144, 255, 0.15)',
                      pointerEvents: 'none',
                      zIndex: 999,
                    }}
                  />
                )}

                {/* Events */}
                {calculateOverlapLayout(dayEvents, parse).map(
                  ({ event, column, totalColumns }) => {
                    const position = getEventPosition(event, day, parse);
                    const { airplane, instructor, student } =
                      getEventDetails(event);
                    const eventColor = getEventColor(event);

                    const isFullWidth = totalColumns === 1;
                    let leftPosition: string;
                    let width: string;

                    if (isFullWidth) {
                      leftPosition = '8px';
                      width = 'calc(100% - 16px)';
                    } else {
                      const gapBetweenColumns = 2;
                      const availableWidth = 'calc(100% - 16px)';
                      const columnWidthCalc = `calc((${availableWidth} - ${(totalColumns - 1) * gapBetweenColumns}px) / ${totalColumns})`;
                      const leftOffsetCalc = `calc(8px + ${column} * (${columnWidthCalc} + ${gapBetweenColumns}px))`;

                      leftPosition = leftOffsetCalc;
                      width = columnWidthCalc;
                    }

                    const eventEndTime = parse(event.end_time);
                    const isFinished = eventEndTime.isBefore(currentTime);

                    return (
                      <Tooltip
                        key={event.id}
                        title={generateEventTooltip(
                          event,
                          formatTime,
                          formatDate,
                          timezone
                        )}
                        placement="right"
                        color="rgba(0, 0, 0, 0.85)"
                        open={draggedEvent ? false : undefined}
                      >
                        <div
                          className="calendar-event"
                          style={{
                            top: `${position.top}px`,
                            height: `${position.height}px`,
                            left: leftPosition,
                            width: width,
                            background: eventColor,
                            borderLeftColor: eventColor,
                            opacity: isFinished ? 0.5 : 1,
                            cursor: onEventUpdate ? 'move' : 'pointer',
                          }}
                          onClick={() => onEventClick && onEventClick(event)}
                          draggable={!!onEventUpdate}
                          onDragStart={(e) =>
                            handleEventDragStart(event, dayIndex, e)
                          }
                          onDragEnd={() => {
                            setDraggedEvent(null);
                            draggedEventRef.current = null;
                            setDropPreview(null);
                          }}
                        >
                          {/* Header: time on left, stacked avatars on right */}
                          <div className="event-header">
                            <div className="event-time">
                              {formatTime(event.start_time, 'HH:mm')} -{' '}
                              {formatTime(event.end_time, 'HH:mm')}
                            </div>
                            {/* Stacked avatars: student in front, instructor behind */}
                            {(student || instructor) && (
                              <div className="event-avatars">
                                {instructor && (
                                  <PersonAvatar
                                    personId={instructor.resource_id}
                                    personName={instructor.resource_name}
                                    size={28}
                                    backgroundColor="#667eea"
                                    style={{
                                      position: 'absolute',
                                      right: 10,
                                      top: 0,
                                      border: '2px solid rgba(0,0,0,0.3)',
                                    }}
                                  />
                                )}
                                {student && (
                                  <PersonAvatar
                                    personId={student.resource_id}
                                    personName={student.resource_name}
                                    size={28}
                                    backgroundColor="#52c41a"
                                    style={{
                                      position: 'absolute',
                                      right: 0,
                                      top: 0,
                                      border: '2px solid rgba(0,0,0,0.3)',
                                    }}
                                  />
                                )}
                              </div>
                            )}
                          </div>

                          {/* Title: just student name */}
                          <div className="event-title">
                            {student?.resource_name || event.title}
                          </div>

                          {/* Airplane name at the bottom when there's space */}
                          {airplane && position.height > 60 && (
                            <div className="event-airplane">
                              ✈️ {airplane.resource_name}
                            </div>
                          )}
                        </div>
                      </Tooltip>
                    );
                  }
                )}

                {/* Current time indicator (only on today's column) */}
                {isTodayDay && currentTimePosition !== null && (
                  <Tooltip
                    title={`Trenutno vreme: ${dayjs().tz(timezone).format('HH:mm')}`}
                    placement="top"
                  >
                    <div
                      className="current-time-indicator"
                      style={{ top: `${currentTimePosition}px` }}
                    />
                  </Tooltip>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
